import React, { useRef, useState } from 'react';
import LidReveal, { LID_SWING_MS, LID_HOLD_MS } from './LidReveal';

/**
 * Painted objects that are also buttons.
 *
 * This reverses an earlier rule on purpose (decision 4 said real buttons in a
 * layout zone, never hotspots pinned to painted objects). The worry behind that
 * rule was sound -- artwork crops differently on every screen, and a hotspot
 * placed in viewport pixels drifts off its motif -- so this component exists
 * to make that impossible rather than merely unlikely:
 *
 *   The layer below has EXACTLY the frame the background has (same fixed
 *   inset, same max-width, same centring). Inside it sits a box the size of
 *   the drawn image, computed with container units the way `cover` computes
 *   it, and offset by the same focus point as `background-position`. Each
 *   object is placed in percent of that box, measured from the master.
 *
 * So the button is always wherever the picture is: through rotation, the
 * address bar growing and shrinking, and browser zoom, with no measuring
 * script to fall out of step. Where the picture is cropped, the button is
 * clipped with it -- only what can be seen can be pressed.
 *
 * Objects must stay in live DOM order after the page's own controls in
 * importance; real buttons that overlap the scene sit above it (z-20).
 *
 * `aspect` is the artwork's width / height. `focusX` must equal the one the
 * background uses. Each object: { key, label, box: [x0, x1, y0, y1] (fractions
 * of the image), onActivate, hint }.
 */

const reducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
};

// Long enough to see the light answer the tap, short enough not to wait on.
const RESPONSE_MS = 200;

const SceneObject = ({ label, hint, box, onActivate, testid, reveal, art, scrim }) => {
  const [lit, setLit] = useState(false);
  const [opening, setOpening] = useState(false);
  const busy = useRef(false);
  const [x0, x1, y0, y1] = box;
  // Rounded, so the style reads 24% rather than 24.000000000000004%.
  const pct = (v) => Math.round(v * 100000) / 1000;
  const cx = pct((x0 + x1) / 2);
  const cy = pct((y0 + y1) / 2);
  const w = pct(x1 - x0);
  const h = pct(y1 - y0);

  const activate = () => {
    // One activation only: a quick second tap during the light must not
    // navigate twice or leave anything half-open.
    if (busy.current) return;
    busy.current = true;
    if (reducedMotion()) {
      onActivate();
      return;
    }
    if (reveal) {
      // Prototype: the painting swings open first (LidReveal).
      setOpening(true);
      setTimeout(() => onActivate(), LID_SWING_MS + LID_HOLD_MS);
      return;
    }
    setLit(true);
    setTimeout(() => onActivate(), RESPONSE_MS);
  };

  return (
    <>
    {opening && reveal && (
      <LidReveal box={box} art={art} scrim={scrim} hinge={reveal.hinge} testid={testid} />
    )}
    <button
      type="button"
      onClick={activate}
      aria-label={label}
      data-testid={testid}
      className={[
        'group pointer-events-auto absolute rounded-md transition-shadow duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--gold))] focus-visible:ring-offset-2 focus-visible:ring-offset-black',
        'motion-reduce:transition-none',
      ].join(' ')}
      style={{
        // Centred on the motif, never smaller than a 44px target.
        left: `calc(${cx}% - max(${w}%, 44px) / 2)`,
        width: `max(${w}%, 44px)`,
        top: `calc(${cy}% - max(${h}%, 44px) / 2)`,
        height: `max(${h}%, 44px)`,
        boxShadow: lit
          ? '0 0 0 1px hsl(var(--gold) / 0.9), 0 0 28px 6px hsl(var(--gold) / 0.35), inset 0 0 40px hsl(var(--gold) / 0.25)'
          : undefined,
      }}
    >
      {hint && !opening && (
        // Live text, anchored to the side of the motif that stays on screen
        // and reaching a little past it onto the wall: on a phone the visible
        // part of the painting is only 90-110px wide, and a label centred in
        // it, or kept inside it, would run off the left edge of the screen.
        <span
          className={[
            'absolute bottom-2 -right-2 rounded-full border px-2 py-1',
            'text-[10px] font-semibold uppercase tracking-[0.08em] whitespace-nowrap',
            'bg-black/60 backdrop-blur-sm transition-colors',
            'group-hover:bg-black/75',
          ].join(' ')}
          style={{ color: 'hsl(var(--gold-pale))', borderColor: 'hsl(var(--gold) / 0.55)' }}
          aria-hidden="true"
        >
          {hint}
        </span>
      )}
    </button>
    </>
  );
};

const SceneObjects = ({ aspect, focusX = 0.5, objects, art, scrim }) => (
  <div
    className="pointer-events-none fixed inset-0 z-10 overflow-hidden"
    style={{
      // Must match the background layer in AppShell exactly.
      maxWidth: 'min(100vw, 56.25vh)',
      margin: '0 auto',
      containerType: 'size',
    }}
    data-testid="scene-objects"
  >
    <div
      className="absolute"
      style={{
        // `cover`: the larger of fitting the width and fitting the height.
        width: `max(100cqw, calc(100cqh * ${aspect}))`,
        height: `max(100cqh, calc(100cqw / ${aspect}))`,
        // background-position: <focusX> center.
        left: `${focusX * 100}%`,
        top: '50%',
        transform: `translate(-${focusX * 100}%, -50%)`,
      }}
    >
      {objects.map((o) => (
        <SceneObject
          key={o.key}
          label={o.label}
          hint={o.hint}
          box={o.box}
          onActivate={o.onActivate}
          reveal={o.reveal}
          art={art}
          scrim={scrim}
          testid={`room-object-${o.key}`}
        />
      ))}
    </div>
  </div>
);

export default SceneObjects;
